// src/pages/ViewRequirement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { requirementApi } from '../../feddback-services/api';
import './ViewRequirement.css';

const ViewRequirement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    location: '',
    q: '',
    job_type: '',
  });
  const [searchTimer, setSearchTimer] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  // ============= AUTHENTICATION =============
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    const name = localStorage.getItem('userName') || 'User';
    
    setIsLoggedIn(!!token);
    setUserRole(role);
    setUserName(name);
    fetchRequirements();
  }, []);

  // ============= DEBOUNCED SEARCH =============
  useEffect(() => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    const timer = setTimeout(() => {
      fetchRequirements();
    }, 500);

    setSearchTimer(timer);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [filters]);

  // ============= KEYBOARD SHORTCUTS =============
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showModal) handleCloseModal();
        if (showApplyModal) handleCloseApplyModal();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showModal, showApplyModal]);

  // ============= API FUNCTIONS =============
  const fetchRequirements = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const queryParams = { ...filters };
      Object.keys(queryParams).forEach(key => {
        if (!queryParams[key]) delete queryParams[key];
      });
      
      // Add pagination
      queryParams.page = page;
      queryParams.limit = pagination.limit;
      
      console.log('🔍 Searching with filters:', queryParams);
      
      const response = await requirementApi.list(queryParams);
      const data = response.data.data || response.data;
      setRequirements(data.items || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || data.items?.length || 0,
        page: page
      }));
    } catch (err) {
      console.error('Error fetching requirements:', err);
      setError(err.response?.data?.message || 'Failed to load requirements. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // ============= HANDLER FUNCTIONS =============
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      department: '',
      location: '',
      q: '',
      job_type: '',
    });
    setTimeout(() => fetchRequirements(), 100);
  };

  const handleRegister = () => {
    navigate('/register');
  };

  const handleViewDetails = async (id) => {
    setModalLoading(true);
    setShowModal(true);
    try {
      const response = await requirementApi.get(id);
      const data = response.data.data || response.data;
      setSelectedRequirement(data);
      setSelectedLocation('');
    } catch (err) {
      console.error('Error fetching requirement details:', err);
      setError('Failed to load requirement details. Please try again.');
      setShowModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequirement(null);
    setSelectedLocation('');
  };

  const handleEdit = (id) => {
    navigate(`/admin/edit-requirement/${id}`);
  };

  const handleApplyClick = (req) => {
    setSelectedRequirement(req);
    setSelectedLocation('');
    setShowApplyModal(true);
  };

const handleConfirmApply = async () => {
  if (!selectedLocation) {
    alert('Please select a location for the interview');
    return;
  }
  
  // ✅ Verify selected location still open
  const openLocs = getLocationStrings(selectedRequirement);
  const isValid = openLocs.some(
    (l) => l.toLowerCase() === selectedLocation.toLowerCase()
  );
  if (!isValid) {
    alert('Selected location is no longer available. Please choose another.');
    return;
  }

  setApplyLoading(true);
  try {
    navigate(`/user/apply/${selectedRequirement.id}?location=${encodeURIComponent(selectedLocation)}`);
  } catch (error) {
    console.error('Navigation error:', error);
    setError('Failed to navigate to application. Please try again.');
    setApplyLoading(false);
  }
};

  const handleCloseApplyModal = () => {
    setShowApplyModal(false);
    setSelectedRequirement(null);
    setSelectedLocation('');
    setApplyLoading(false);
  };

  // ============= HELPER FUNCTIONS =============
// ✅ Fakt OPEN locations return karto (is_open === true)
const getLocationStrings = useCallback((req) => {
  if (!req) return [];

  // ✅ Locations array check kara
  const locationSources = [
    req.locations,
    req.open_locations,       // ✅ Backend cha pre-filtered array
    req.locations_data,
    req.locations_list
  ];

  for (const source of locationSources) {
    if (!source) continue;

    if (Array.isArray(source)) {
      const locations = source
        .map(loc => {
          if (typeof loc === 'string') {
            // ✅ String location = assume open
            return { name: loc.trim(), is_open: true };
          }
          if (typeof loc === 'object' && loc !== null) {
            // ✅ Strict check: is_open === true
            const isOpen =
              loc.is_open !== undefined
                ? loc.is_open === true
                : loc.enabled !== undefined
                ? loc.enabled === true
                : loc.is_selectable !== undefined
                ? loc.is_selectable === true
                : true; // fallback

            return {
              name: loc.name || loc.location || loc.city || loc.address || '',
              is_open: isOpen
            };
          }
          return { name: '', is_open: false };
        })
        // ✅ Fakt open locations ani valid names
        .filter(loc => loc.name && loc.is_open === true)
        .map(loc => loc.name);

      if (locations.length > 0) return locations;
    }
  }

  // ✅ Fallback: jar `location` string asel tar (legacy data)
  // Pan he fakt tab vapraycha jevha `locations` array nahi
  if (req.location && typeof req.location === 'string') {
    // Jar locations array empty asel tar, mala open_locations check kar
    if (Array.isArray(req.open_locations) && req.open_locations.length > 0) {
      return req.open_locations
        .map(l => (typeof l === 'string' ? l : l.name))
        .filter(Boolean);
    }
    // ⚠️ Legacy: fakt string location (open asa assume)
    if (req.location.includes(',')) {
      return req.location.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [req.location];
  }

  return [];
}, []);

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { class: 'status-draft', label: 'Draft' },
      'open': { class: 'status-open', label: 'Open' },
      'closed': { class: 'status-closed', label: 'Closed' },
      'active': { class: 'status-active', label: 'Active' }
    };
    const s = statusMap[status?.toLowerCase()] || statusMap['draft'];
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFieldTypeIcon = (type) => {
    const icons = {
      text: 'fa-font',
      number: 'fa-hashtag',
      email: 'fa-envelope',
      phone: 'fa-phone',
      date: 'fa-calendar',
      dropdown: 'fa-caret-down',
      textarea: 'fa-align-left',
      file: 'fa-file',
      boolean: 'fa-check-square',
    };
    return icons[type] || 'fa-cog';
  };

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Text',
      number: 'Number',
      email: 'Email',
      phone: 'Phone',
      date: 'Date',
      dropdown: 'Dropdown',
      textarea: 'Textarea',
      file: 'File',
      boolean: 'Boolean',
    };
    return labels[type] || type;
  };

  // ============= MEMOIZED VALUES =============
  const isAdmin = useMemo(() => 
    isLoggedIn && ['admin', 'super_admin', 'external_admin'].includes(userRole),
    [isLoggedIn, userRole]
  );

  const isCandidate = useMemo(() => 
    isLoggedIn && userRole === 'candidate',
    [isLoggedIn, userRole]
  );

  // ============= RENDER =============
  return (
    <div className="jmk-view-requirements">
      <style>{`
        .jmk-view-requirements {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #1a2422;
          padding: 0;
        }
        .jmk-register-text {
          color: #666;
          font-size: 14px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .jmk-register-text i {
          color: #007bff;
        }
        .jmk-req-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .jmk-req-header-left h1 {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          color: #00524f;
          font-weight: 700;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .jmk-req-header-left h1 i {
          color: #008986;
          font-size: 24px;
        }
        .jmk-req-header-left p {
          color: #5c6c6a;
          font-size: 14px;
          margin: 4px 0 0 0;
        }
        .jmk-req-header-right {
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
        }
        .jmk-btn-primary {
          background: linear-gradient(135deg, #00524f 0%, #008986 50%, #00a89e 100%);
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          text-decoration: none;
        }
        .jmk-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 137, 134, 0.3);
        }
        .jmk-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .jmk-btn-secondary {
          background: #eaf3f2;
          color: #00524f;
          border: 1px solid #cddad8;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        .jmk-btn-secondary:hover {
          background: #d4edeb;
          border-color: #008986;
        }
        .jmk-btn-login {
          background: #008986;
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          text-decoration: none;
        }
        .jmk-btn-login:hover {
          background: #00524f;
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 137, 134, 0.3);
        }
        .register-btn {
          background: #ffffff;
          color: #00524f;
          border: 2px solid #008986;
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        .register-btn:hover {
          background: #008986;
          color: #ffffff;
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 137, 134, 0.2);
        }
        .jmk-filter-bar {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 24px;
          padding: 16px 20px;
          background: #f0f4f3;
          border-radius: 10px;
          border: 1px solid #cddad8;
          align-items: center;
        }
        .jmk-filter-group {
          flex: 1;
          min-width: 140px;
          position: relative;
        }
        .jmk-filter-input,
        .jmk-filter-select {
          width: 100%;
          padding: 9px 14px;
          border: 1px solid #cddad8;
          border-radius: 6px;
          font-size: 13px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
          background: #ffffff;
          color: #1a2422;
        }
        .jmk-filter-input:focus,
        .jmk-filter-select:focus {
          outline: none;
          border-color: #008986;
          box-shadow: 0 0 0 3px rgba(0, 137, 134, 0.1);
        }
        .jmk-filter-input::placeholder {
          color: #9aa8a6;
        }
        .jmk-filter-clear-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9aa8a6;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
        }
        .jmk-filter-clear-btn:hover {
          color: #5c6c6a;
        }
        .jmk-btn-clear {
          background: #ffffff;
          border: 1px solid #cddad8;
          padding: 9px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          color: #5c6c6a;
        }
        .jmk-btn-clear:hover {
          background: #f0f4f3;
          border-color: #b0bfbd;
        }
        .jmk-req-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 20px;
        }
        @media (max-width: 768px) {
          .jmk-req-grid { grid-template-columns: 1fr; }
        }
        .jmk-req-card {
          background: #ffffff;
          border: 1px solid #cddad8;
          border-radius: 10px;
          padding: 20px;
          transition: all 0.3s ease;
        }
        .jmk-req-card:hover {
          border-color: #008986;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transform: translateY(-3px);
        }
        .jmk-req-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        .jmk-req-card-title {
          font-family: 'Playfair Display', serif;
          font-size: 17px;
          font-weight: 700;
          color: #00524f;
          margin: 0;
          flex: 1;
        }
        .jmk-req-card-code {
          font-size: 11px;
          color: #9aa8a6;
          font-weight: 500;
        }
        .jmk-req-card-desc {
          color: #5c6c6a;
          font-size: 13px;
          line-height: 1.6;
          margin: 8px 0 12px;
        }
        .jmk-req-card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
          padding: 10px 0;
          border-top: 1px solid #eaf3f2;
          border-bottom: 1px solid #eaf3f2;
          margin-bottom: 12px;
        }
        .jmk-req-card-details span {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #5c6c6a;
        }
        .jmk-req-card-details i {
          color: #008986;
          width: 14px;
        }
        .jmk-req-card-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .jmk-btn-view {
          background: #eaf3f2;
          color: #00524f;
          border: none;
          padding: 7px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          flex: 1;
          justify-content: center;
        }
        .jmk-btn-view:hover {
          background: #d4edeb;
        }
        .jmk-btn-apply {
          background: #008986;
          color: #fff;
          border: none;
          padding: 7px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
          flex: 1;
          justify-content: center;
        }
        .jmk-btn-apply:hover:not(:disabled) {
          background: #00524f;
        }
        .jmk-btn-apply:disabled {
          background: #cddad8;
          cursor: not-allowed;
        }
        .jmk-btn-edit {
          background: #fff8e1;
          color: #f57c00;
          border: none;
          padding: 7px 14px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s ease;
          font-family: 'Inter', sans-serif;
        }
        .jmk-btn-edit:hover {
          background: #ffecb3;
        }
        .status-badge {
          display: inline-block;
          padding: 3px 12px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .status-draft {
          background: #eaf3f2;
          color: #5c6c6a;
        }
        .status-open,
        .status-active {
          background: #d4edda;
          color: #155724;
        }
        .status-closed {
          background: #f8d7da;
          color: #721c24;
        }
        .jmk-fields-preview {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          padding: 8px 0;
          border-top: 1px solid #eaf3f2;
        }
        .jmk-fields-label {
          font-size: 12px;
          font-weight: 600;
          color: #5c6c6a;
        }
        .jmk-field-tag {
          font-size: 11px;
          background: #ebf4ff;
          color: #2b6cb0;
          padding: 2px 10px;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .jmk-field-tag i {
          font-size: 10px;
        }
        .jmk-field-more {
          font-size: 11px;
          color: #718096;
        }
        .location-multi {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }
        .location-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
        }
        .location-chip-small {
          background: #e3f2fd;
          color: #0d6efd;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          border: 1px solid #b6d4fe;
        }
        .location-chip-more {
          font-size: 11px;
          color: #6c757d;
          background: #e9ecef;
          padding: 2px 8px;
          border-radius: 12px;
        }
        .location-chips-modal {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .location-chip-modal {
          background: #e3f2fd;
          color: #0d6efd;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 13px;
          border: 1px solid #b6d4fe;
        }
        .jmk-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .jmk-modal-content {
          background: #ffffff;
          border-radius: 12px;
          width: 90%;
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease;
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .jmk-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 24px;
          border-bottom: 1px solid #eaf3f2;
          position: sticky;
          top: 0;
          background: #ffffff;
          z-index: 1;
          border-radius: 12px 12px 0 0;
        }
        .jmk-modal-header h2 {
          font-family: 'Playfair Display', serif;
          color: #00524f;
          font-size: 20px;
          margin: 0;
        }
        .jmk-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          color: #9aa8a6;
          padding: 4px;
          font-size: 22px;
          transition: color 0.3s ease;
        }
        .jmk-modal-close:hover {
          color: #1a2422;
        }
        .jmk-modal-body {
          padding: 24px;
        }
        .jmk-detail-section {
          margin-bottom: 20px;
        }
        .jmk-detail-section h3 {
          font-size: 14px;
          color: #00524f;
          font-weight: 700;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 2px solid #eaf3f2;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .jmk-detail-row {
          display: flex;
          padding: 6px 0;
          gap: 12px;
        }
        .jmk-detail-label {
          font-weight: 600;
          color: #5c6c6a;
          min-width: 100px;
          font-size: 13px;
        }
        .jmk-detail-value {
          color: #1a2422;
          font-size: 13px;
        }
        .jmk-detail-description {
          background: #f8fafa;
          padding: 14px;
          border-radius: 6px;
          line-height: 1.7;
          font-size: 13px;
          color: #1a2422;
          white-space: pre-wrap;
          max-height: 200px;
          overflow-y: auto;
        }
        .jmk-detail-fields {
          display: grid;
          gap: 8px;
        }
        .jmk-detail-field {
          background: #f8fafa;
          padding: 10px 14px;
          border-radius: 6px;
          border-left: 3px solid #008986;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .jmk-detail-field .field-name {
          font-weight: 500;
          color: #1a2422;
          font-size: 13px;
        }
        .jmk-detail-field .field-type {
          background: #eaf3f2;
          color: #5c6c6a;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
        }
        .jmk-detail-field .required-badge {
          background: #008986;
          color: #fff;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
        }
        .jmk-modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eaf3f2;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          position: sticky;
          bottom: 0;
          background: #ffffff;
          border-radius: 0 0 12px 12px;
        }
        .jmk-modal-footer .jmk-btn-primary {
          padding: 8px 20px;
          font-size: 13px;
        }
        .jmk-modal-footer .jmk-btn-secondary {
          padding: 8px 20px;
          font-size: 13px;
        }
        .jmk-location-select-modal .jmk-modal-content {
          max-width: 500px;
        }
        .jmk-location-select {
          margin: 16px 0;
        }
        .jmk-location-select label {
          display: block;
          font-weight: 600;
          color: #00524f;
          margin-bottom: 8px;
          font-size: 14px;
        }
        .jmk-location-select select {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #cddad8;
          border-radius: 8px;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          background: #ffffff;
          transition: all 0.3s ease;
          color: #1a2422;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236c757d' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
        }
        .jmk-location-select select:focus {
          outline: none;
          border-color: #008986;
          box-shadow: 0 0 0 3px rgba(0, 137, 134, 0.1);
        }
        .jmk-location-select select option {
          padding: 8px;
        }
        .location-hint {
          font-size: 12px;
          color: #5c6c6a;
          margin-top: 6px;
        }
        .location-hint i {
          color: #008986;
          margin-right: 4px;
        }
        .jmk-modal-subtitle {
          font-size: 14px;
          color: #5c6c6a;
          margin-top: 4px;
        }
        .jmk-loading {
          text-align: center;
          padding: 60px 20px;
        }
        .jmk-spinner {
          width: 40px;
          height: 40px;
          margin: 0 auto 16px;
          border: 3px solid #eaf3f2;
          border-top: 3px solid #008986;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .jmk-loading p {
          color: #5c6c6a;
          font-size: 14px;
        }
        .jmk-error {
          text-align: center;
          padding: 40px 20px;
          background: #fff5f5;
          border-radius: 10px;
          border: 1px solid #fcd5d5;
        }
        .jmk-error i {
          font-size: 40px;
          color: #e53e3e;
          display: block;
          margin-bottom: 12px;
        }
        .jmk-error p {
          color: #5c6c6a;
          font-size: 14px;
          margin-bottom: 16px;
        }
        .jmk-btn-retry {
          background: #008986;
          color: #fff;
          border: none;
          padding: 8px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
        }
        .jmk-btn-retry:hover {
          background: #00524f;
        }
        .jmk-empty {
          text-align: center;
          padding: 60px 20px;
          background: #f8fafa;
          border-radius: 10px;
          border: 2px dashed #cddad8;
        }
        .jmk-empty i {
          font-size: 48px;
          color: #cddad8;
          display: block;
          margin-bottom: 16px;
        }
        .jmk-empty h3 {
          color: #00524f;
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          margin-bottom: 6px;
        }
        .jmk-empty p {
          color: #5c6c6a;
          font-size: 14px;
        }
        .jmk-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 24px;
          padding: 16px 0;
        }
        .jmk-pagination button {
          padding: 8px 16px;
          border: 1px solid #cddad8;
          border-radius: 6px;
          background: #ffffff;
          color: #00524f;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .jmk-pagination button:hover:not(:disabled) {
          background: #008986;
          color: #ffffff;
          border-color: #008986;
        }
        .jmk-pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .jmk-pagination span {
          color: #5c6c6a;
          font-size: 14px;
        }
        @media (max-width: 768px) {
          .jmk-req-header {
            flex-direction: column;
            align-items: stretch;
          }
          .jmk-req-header-right {
            flex-direction: column;
            width: 100%;
          }
          .jmk-req-header-right button {
            width: 100%;
            justify-content: center;
          }
          .jmk-filter-bar {
            flex-direction: column;
          }
          .jmk-filter-group {
            min-width: 100%;
          }
          .jmk-req-card-details {
            grid-template-columns: 1fr;
          }
          .jmk-req-card-actions {
            flex-direction: column;
          }
          .jmk-modal-content {
            width: 95%;
            margin: 10px;
          }
          .jmk-detail-row {
            flex-direction: column;
            gap: 2px;
          }
          .jmk-detail-label {
            min-width: auto;
          }
          .jmk-location-select-modal .jmk-modal-content {
            margin: 10px;
            width: 95%;
          }
        }
        @media (max-width: 480px) {
          .jmk-req-header-left h1 {
            font-size: 22px;
          }
        }
      `}</style>

      {/* ============= HEADER ============= */}
      <div className="jmk-req-header">
        <div className="jmk-req-header-left">
          <h1>
            <i className="fas fa-list"></i>
            Available Positions
          </h1>
          <p>
            {isLoggedIn ? (
              <>Welcome, {userName} • {isCandidate ? 'Candidate' : isAdmin ? 'Admin' : 'User'} • Total: {requirements.length}</>
            ) : (
              <>Browse our latest job opportunities • Total: {requirements.length}</>
            )}
          </p>
        </div>
        <div className="jmk-req-header-right">
          {/* ✅ Register button - ONLY on home page (/) and NOT logged in */}
          {!isLoggedIn && location.pathname === '/' && (
            <button 
              type="button" 
              className="register-btn"
              onClick={handleRegister}
            >
              <i className="fas fa-user-plus"></i>
              Register as Candidate
            </button>
          )}
        </div>
      </div>

      {/* ============= FILTER BAR ============= */}
      <div className="jmk-filter-bar">
        <div className="jmk-filter-group">
          <input
            type="text"
            name="q"
            value={filters.q}
            onChange={handleFilterChange}
            placeholder="Search jobs by title, department..."
            className="jmk-filter-input"
            aria-label="Search jobs"
          />
          {filters.q && (
            <button 
              className="jmk-filter-clear-btn" 
              onClick={() => setFilters(prev => ({ ...prev, q: '' }))}
              aria-label="Clear search"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="jmk-filter-group">
          <input
            type="text"
            name="department"
            value={filters.department}
            onChange={handleFilterChange}
            placeholder="Department..."
            className="jmk-filter-input"
            aria-label="Filter by department"
          />
          {filters.department && (
            <button 
              className="jmk-filter-clear-btn" 
              onClick={() => setFilters(prev => ({ ...prev, department: '' }))}
              aria-label="Clear department filter"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="jmk-filter-group">
          <input
            type="text"
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            placeholder="Location..."
            className="jmk-filter-input"
            aria-label="Filter by location"
          />
          {filters.location && (
            <button 
              className="jmk-filter-clear-btn" 
              onClick={() => setFilters(prev => ({ ...prev, location: '' }))}
              aria-label="Clear location filter"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="jmk-filter-group">
          <select
            name="job_type"
            value={filters.job_type}
            onChange={handleFilterChange}
            className="jmk-filter-select"
            aria-label="Filter by job type"
          >
            <option value="">All Types</option>
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Contract">Contract</option>
            <option value="Internship">Internship</option>
          </select>
        </div>
        <button className="jmk-btn-clear" onClick={clearFilters}>
          <i className="fas fa-times"></i> Clear All
        </button>
      </div>

      {/* ============= LOADING STATE ============= */}
      {loading && (
        <div className="jmk-loading" role="status" aria-live="polite">
          <div className="jmk-spinner"></div>
          <p>Loading requirements...</p>
        </div>
      )}

      {/* ============= ERROR STATE ============= */}
      {error && !loading && (
        <div className="jmk-error" role="alert">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button className="jmk-btn-retry" onClick={() => fetchRequirements()}>
            Retry
          </button>
        </div>
      )}

      {/* ============= EMPTY STATE ============= */}
      {!loading && !error && requirements.length === 0 && (
        <div className="jmk-empty">
          <i className="fas fa-bullhorn"></i>
          <h3>No Requirements Found</h3>
          <p>Check back later for new opportunities</p>
          {Object.values(filters).some(v => v !== '') && (
            <button 
              className="jmk-btn-primary" 
              onClick={clearFilters}
              style={{ marginTop: '16px' }}
            >
              <i className="fas fa-undo"></i> Clear Filters
            </button>
          )}
        </div>
      )}

      {/* ============= REQUIREMENTS GRID ============= */}
      {!loading && !error && requirements.length > 0 && (
        <>
          <div className="jmk-req-grid">
            {requirements.map((req) => {
              const locations = getLocationStrings(req);
              
              return (
                <div key={req.id} className="jmk-req-card">
                  <div className="jmk-req-card-header">
                    <h3 className="jmk-req-card-title">{req.title}</h3>
                    {getStatusBadge(req.status)}
                  </div>
                  <div className="jmk-req-card-code">{req.requirement_code}</div>
                  <p className="jmk-req-card-desc">
                    {req.job_description?.substring(0, 120)}
                    {req.job_description?.length > 120 && '...'}
                  </p>
                  <div className="jmk-req-card-details">
                    {req.department && (
                      <span><i className="fas fa-building"></i> {req.department}</span>
                    )}
                    {locations.length > 0 && (
                      <span className="location-multi">
                        <i className="fas fa-map-marker-alt"></i>
                        <span className="location-chips">
                          {locations.slice(0, 2).map((loc, idx) => (
                            <span key={idx} className="location-chip-small">{loc}</span>
                          ))}
                          {locations.length > 2 && (
                            <span className="location-chip-more">+{locations.length - 2}</span>
                          )}
                        </span>
                      </span>
                    )}
                    {req.job_type && (
                      <span><i className="fas fa-briefcase"></i> {req.job_type}</span>
                    )}
                    {(req.experience_min || req.experience_max) && (
                      <span><i className="fas fa-clock"></i> {req.experience_min || 0} - {req.experience_max || 'Any'} yrs</span>
                    )}
                  </div>

                  {req.fields && req.fields.length > 0 && (
                    <div className="jmk-fields-preview">
                      <span className="jmk-fields-label">Fields:</span>
                      {req.fields.slice(0, 3).map((field, idx) => (
                        <span key={idx} className="jmk-field-tag">
                          <i className={`fas ${getFieldTypeIcon(field.field_type)}`}></i>
                          {field.field_name}
                          {field.is_required && <span style={{ color: '#e53e3e', marginLeft: '2px' }}>*</span>}
                        </span>
                      ))}
                      {req.fields.length > 3 && (
                        <span className="jmk-field-more">+{req.fields.length - 3} more</span>
                      )}
                    </div>
                  )}

                  <div className="jmk-req-card-actions">
                    <button 
                      className="jmk-btn-view" 
                      onClick={() => handleViewDetails(req.id)}
                      aria-label={`View details for ${req.title}`}
                    >
                      <i className="fas fa-eye"></i> View Details
                    </button>
                 {isCandidate && req.status === 'open' && (
  <button 
    className="jmk-btn-apply" 
    onClick={() => handleApplyClick(req)}
    disabled={getLocationStrings(req).length === 0}
    aria-label={`Apply for ${req.title}`}
  >
    <i className="fas fa-paper-plane"></i> 
    {getLocationStrings(req).length === 0 ? 'No Openings' : 'Apply Now'}
  </button>
)}
                    {/* {isAdmin && (
                      <button 
                        className="jmk-btn-edit" 
                        onClick={() => handleEdit(req.id)}
                        aria-label={`Edit ${req.title}`}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                    )} */}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ============= PAGINATION ============= */}
          {pagination.total > pagination.limit && (
            <div className="jmk-pagination">
              <button
                onClick={() => fetchRequirements(pagination.page - 1)}
                disabled={pagination.page === 1}
                aria-label="Previous page"
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>
              <span>
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
              </span>
              <button
                onClick={() => fetchRequirements(pagination.page + 1)}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                aria-label="Next page"
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* ============= LOCATION SELECTION MODAL ============= */}
      {showApplyModal && selectedRequirement && (
        <div 
          className="jmk-modal-overlay jmk-location-select-modal" 
          onClick={handleCloseApplyModal}
          role="dialog"
          aria-modal="true"
          aria-label="Select interview location"
        >
          <div className="jmk-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="jmk-modal-header">
              <div>
                <h2>Apply for {selectedRequirement.title}</h2>
                <p className="jmk-modal-subtitle">Select your preferred interview location</p>
              </div>
              <button 
                className="jmk-modal-close" 
                onClick={handleCloseApplyModal}
                aria-label="Close apply modal"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="jmk-modal-body">
              <div className="jmk-location-select">
                <label htmlFor="locationSelect">
                  <i className="fas fa-map-marker-alt" style={{ color: '#008986' }}></i> Select Interview Location
                </label>
                <select
                  id="locationSelect"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="location-select-dropdown"
                  aria-label="Choose interview location"
                >
                  <option value="">-- Select a location --</option>
                  {getLocationStrings(selectedRequirement).map((loc, idx) => (
                    <option key={idx} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                <div className="location-hint">
                  <i className="fas fa-info-circle"></i>
                  {getLocationStrings(selectedRequirement).length === 0 
                    ? 'No locations available for this position' 
                    : `Available locations: ${getLocationStrings(selectedRequirement).join(', ')}`}
                </div>
              </div>

              {selectedRequirement.job_description && (
                <div className="jmk-detail-section" style={{ marginTop: '16px' }}>
                  <h3>Job Description</h3>
                  <div className="jmk-detail-description" style={{ maxHeight: '120px' }}>
                    {selectedRequirement.job_description}
                  </div>
                </div>
              )}
            </div>
            <div className="jmk-modal-footer">
              <button 
                className="jmk-btn-secondary" 
                onClick={handleCloseApplyModal}
                disabled={applyLoading}
              >
                Cancel
              </button>
              <button 
                className="jmk-btn-primary" 
                onClick={handleConfirmApply}
                disabled={!selectedLocation || applyLoading}
                aria-label="Confirm application"
              >
                {applyLoading ? (
                  <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Apply Now</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============= DETAILS MODAL ============= */}
      {showModal && selectedRequirement && !showApplyModal && (
        <div 
          className="jmk-modal-overlay" 
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
          aria-label="Requirement details"
        >
          <div className="jmk-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="jmk-modal-header">
              <h2>{selectedRequirement.title}</h2>
              <button 
                className="jmk-modal-close" 
                onClick={handleCloseModal}
                aria-label="Close details modal"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="jmk-modal-body">
              {modalLoading ? (
                <div className="jmk-loading">
                  <div className="jmk-spinner"></div>
                  <p>Loading details...</p>
                </div>
              ) : (
                <>
                  <div className="jmk-detail-section">
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Code:</span>
                      <span className="jmk-detail-value">{selectedRequirement.requirement_code}</span>
                    </div>
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Status:</span>
                      {getStatusBadge(selectedRequirement.status)}
                    </div>
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Department:</span>
                      <span className="jmk-detail-value">{selectedRequirement.department || 'N/A'}</span>
                    </div>
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Locations:</span>
                      <span className="jmk-detail-value">
                        {getLocationStrings(selectedRequirement).length > 0 ? (
                          <div className="location-chips-modal">
                            {getLocationStrings(selectedRequirement).map((loc, idx) => (
                              <span key={idx} className="location-chip-modal">{loc}</span>
                            ))}
                          </div>
                        ) : 'N/A'}
                      </span>
                    </div>
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Job Type:</span>
                      <span className="jmk-detail-value">{selectedRequirement.job_type || 'N/A'}</span>
                    </div>
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Experience:</span>
                      <span className="jmk-detail-value">
                        {selectedRequirement.experience_min || 0} - {selectedRequirement.experience_max || 'Any'} years
                      </span>
                    </div>
                    <div className="jmk-detail-row">
                      <span className="jmk-detail-label">Created:</span>
                      <span className="jmk-detail-value">{formatDate(selectedRequirement.created_at)}</span>
                    </div>
                  </div>

                  <div className="jmk-detail-section">
                    <h3>Job Description</h3>
                    <div className="jmk-detail-description">
                      {selectedRequirement.job_description || 'No description provided'}
                    </div>
                  </div>

                  {selectedRequirement.jd_file_url && (
                    <div className="jmk-detail-section">
                      <h3>Job Description File</h3>
                      <a 
                        href={selectedRequirement.jd_file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="jmk-btn-primary" 
                        style={{ display: 'inline-flex', textDecoration: 'none' }}
                      >
                        <i className="fas fa-file-pdf"></i> Download JD
                      </a>
                    </div>
                  )}

                  {selectedRequirement.fields && selectedRequirement.fields.length > 0 && (
                    <div className="jmk-detail-section">
                      <h3>Application Fields</h3>
                      <div className="jmk-detail-fields">
                        {selectedRequirement.fields.map((field, index) => (
                          <div key={index} className="jmk-detail-field">
                            <span className="field-name">{field.field_name}</span>
                            <span className="field-type">{getFieldTypeLabel(field.field_type)}</span>
                            {field.is_required && <span className="required-badge">Required</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="jmk-modal-footer">
              {isCandidate && selectedRequirement?.status === 'open' && (
                <button 
                  className="jmk-btn-primary" 
                  onClick={() => {
                    handleCloseModal();
                    handleApplyClick(selectedRequirement);
                  }}
                  aria-label="Apply for this position"
                >
                  <i className="fas fa-paper-plane"></i> Apply Now
                </button>
              )}
              <button 
                className="jmk-btn-secondary" 
                onClick={handleCloseModal}
                aria-label="Close modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewRequirement;